import { Bookmark, X } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"

/**
 * OKS-28 — Applied-segment chip — extends the OKS-25 chip rail.
 *
 * Renders only when `?segment=<id>` is in play. Visual cue is the
 * leading `Bookmark` icon (UX Spec §6 — color is NOT the sole
 * indicator; the chip carries an icon + the "Segment:" prefix label
 * for the WCAG 2.1 AA contrast-floor invariant).
 *
 * **Locked testid:** `crew-list-segment-chip` (UX Spec §2.1 / QA-Plan).
 *
 * Removing the chip calls `onRemove()`, which `Brand/Crew/Index` wires
 * to `useCrewListUrl().setSegment(null)` — clears the segment AND the
 * filter rules that came with it.
 */
export interface AppliedSegmentChipProps {
  segment: { id: number; name: string }
  onRemove: () => void
  className?: string
}

export function AppliedSegmentChip({
  segment,
  onRemove,
  className,
}: AppliedSegmentChipProps) {
  const { t } = useTranslation()

  return (
    <Badge
      variant="secondary"
      data-testid="crew-list-segment-chip"
      data-segment-id={segment.id}
      className={cn("gap-1 pr-1", className)}
    >
      <Bookmark className="size-3" aria-hidden="true" />
      <span>{t("crew_segments.apply.chip_label", { name: segment.name })}</span>
      <button
        type="button"
        data-testid="crew-list-segment-chip-remove"
        aria-label={t("crew_segments.apply.chip_remove_aria", {
          name: segment.name,
        })}
        onClick={onRemove}
        className="hover:bg-muted/50 focus-visible:ring-ring rounded-full p-0.5 focus-visible:ring-2"
      >
        <X className="size-3" aria-hidden="true" />
      </button>
    </Badge>
  )
}
